import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/lib/api";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() }, { auth: false });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't send reset link.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthLayout
        testId="forgot-password-done"
        eyebrow="Reset link sent"
        heading="Check your inbox."
        description="If that email is registered with us, a password reset link is on its way. The link expires in 60 minutes."
      >
        <Button
          asChild
          className="w-full bg-foreground hover:bg-charcoal text-ivory rounded-none h-12"
          data-testid="forgot-back-login"
        >
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      testId="forgot-password-page"
      eyebrow="Forgot password"
      heading="Reset your password."
      description="Enter the email tied to your account and we'll send a reset link."
    >
      <form
        data-testid="forgot-form"
        onSubmit={onSubmit}
        className="space-y-5"
        noValidate
      >
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="forgot-email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            autoComplete="email"
            required
          />
        </div>

        {error && (
          <p
            role="alert"
            data-testid="forgot-error"
            className="text-sm text-oxblood border border-oxblood/30 bg-oxblood/5 px-3 py-2"
          >
            {error}
          </p>
        )}

        <Button
          type="submit"
          size="lg"
          data-testid="forgot-submit"
          disabled={!email || submitting}
          className="w-full bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-12"
        >
          {submitting ? "Sending…" : "Send reset link"}
        </Button>
      </form>
    </AuthLayout>
  );
}
